import * as React from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { ClassicButton, SmallLoader } from '../../components';
import styled, { ThemeContext } from 'styled-components';
import { DownloadIcon } from 'assets/icons';

const ClassicButtonWithoutRightRadius = styled(ClassicButton)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-color: ${({ theme }) => theme.baseBlack};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.baseBlack};
  border-top: 1px solid;
  border-bottom: 1px solid;
  border-left: 1px solid;

  svg {
    width: 1.5rem;
    transition: none;
    transform: none;
    margin-left: 0.25rem;
  }

  :hover {
    background-color: ${({ theme }) => theme.white};
    color: ${({ theme }) => theme.primary};

    svg {
      path {
        fill: ${({ theme }) => theme.primary};
      }
    }
  }
`;

const CustomButton = styled(Button)`
  && {
    background-color: ${({ theme }) => theme.white};
    border-left: 1px solid white;
    border-top: 1px solid ${({ theme }) => theme.baseBlack};
    border-bottom: 1px solid ${({ theme }) => theme.baseBlack};
    border-right: 1px solid ${({ theme }) => theme.baseBlack};
    transition-duration: unset;

    :disabled {
      background-color: ${({ theme }) => theme.disabledButtonBgColor};
      border: 0;
    }
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;

  svg {
    width: 1.5rem;
    transition: none;
    transform: none;
    margin-left: 0.25rem;
  }
`;

interface SplitButtonProps {
  disabled?: boolean;
  label: string;
  options: Array<{ icon?: boolean; label: string; value: number }>;
  width?: string;
  clickHandler: (value?: number) => void;
  icon?: string;
  isLoading?: boolean;
}

export const SplitButton: React.FC<SplitButtonProps> = ({
  disabled = false,
  options,
  label,
  width,
  clickHandler,
  icon,
  isLoading = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const theme = React.useContext(ThemeContext);
  const handleClick = () => {
    return clickHandler();
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) => {
    return clickHandler(options[index].value);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  return (
    <React.Fragment>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="split button"
        disabled={disabled || isLoading}
        sx={{ width: width, boxShadow: 'none' }}
      >
        <ClassicButtonWithoutRightRadius
          width="10rem"
          disabled={disabled || isLoading}
          onClick={handleClick}
        >
          <ButtonContent>
            {!isLoading ? (
              <>
                {label} {icon && 'download' === icon && <DownloadIcon />}
              </>
            ) : (
              <SmallLoader />
            )}
          </ButtonContent>
        </ClassicButtonWithoutRightRadius>

        <CustomButton
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
          disabled={disabled || isLoading}
        >
          <ArrowDropDownIcon />
        </CustomButton>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 1,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        nonce={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper sx={{ backgroundColor: theme.mainBackgroundColor }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.value}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      <ButtonContent>
                        {option.label}
                        {icon && 'download' === icon && <DownloadIcon />}
                      </ButtonContent>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};
